import {HeaderState} from './HeaderState'
import {Box} from '@primer/react'
import {TaskListStatus} from '../shared/TaskListStatus'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {StickyHeaderTitle, type StickyHeaderTitleProps} from './StickyHeaderTitle'
import {HeaderMenu, type HeaderMenuProps} from './HeaderMenu'
import {useMemo, useRef} from 'react'
import {ContentWrapper} from '../ContentWrapper'
import {HeaderIssueType} from './HeaderIssueType'
import type {Header$data} from './__generated__/Header.graphql'
import type {OptionConfig} from '../OptionConfig'
import type {TaskListStatus$key} from '../shared/__generated__/TaskListStatus.graphql'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {LinkedPullRequests} from './LinkedPullRequests'
import {RepositoryPill} from './RepositoryPill'

export type HeaderMetadataProps = {
  headerData: Header$data
  taskListStatusData?: TaskListStatus$key
  optionConfig: OptionConfig
  isSticky: boolean
  stickyStyles: BetterSystemStyleObject
  typeData: Header$data['issueType']
} & HeaderMenuProps &
  StickyHeaderTitleProps

export function HeaderMetadata({
  headerData,
  taskListStatusData,
  optionConfig,
  isSticky,
  typeData,
  ...rest
}: HeaderMetadataProps) {
  const {showRepositoryPill, innerSx} = optionConfig

  const headerContainerRef = useRef<HTMLDivElement>(null)
  const breakpoint = useContainerBreakpoint(headerContainerRef.current)

  const styles = useMemo(() => {
    const sx: BetterSystemStyleObject = {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '64px',
      borderBottom: '1px solid',
      borderColor: isSticky ? 'border.default' : 'transparent',
      position: 'sticky',
      top: 0,
      zIndex: 14,
      backgroundColor: isSticky ? 'canvas.default' : 'transparent',
    }

    return sx
  }, [isSticky])

  return (
    <>
      <Box ref={headerContainerRef} sx={{...styles}}>
        <ContentWrapper sx={{display: 'flex', alignItems: 'center', gap: 2, ...innerSx}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mr: 1}}>
            <HeaderState stateData={headerData} />
            {!isSticky && typeData && <HeaderIssueType name={typeData.name} />}
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {isSticky && (
              <Box sx={{display: 'flex', ml: 1}}>
                <StickyHeaderTitle {...rest} />
              </Box>
            )}

            <Box
              sx={{
                display: breakpoint(['none', 'none', 'flex', 'flex']),
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
              }}
            >
              {showRepositoryPill && <RepositoryPill sx={{maxWidth: '50%'}} repositoryPillData={headerData} />}
              {taskListStatusData && <TaskListStatus taskListStatus={taskListStatusData} />}
              <LinkedPullRequests pullRequestsData={headerData} isSticky={isSticky} />
            </Box>
          </Box>
          {isSticky && (
            <Box>
              <HeaderMenu optionConfig={optionConfig} isSticky={isSticky} {...rest} />
            </Box>
          )}
          {rest.metadataPaneTrigger}
        </ContentWrapper>
      </Box>

      <ContentWrapper
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          ...innerSx,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{width: '100%', borderBottom: '1px solid', borderBottomColor: 'border.subtle'}}>
          <Box
            sx={{
              display: breakpoint(['flex', 'flex', 'none', 'none']),
              alignItems: 'center',
              pb: 3,
              columnGap: 2,
              rowGap: 1,
              flexWrap: 'wrap',
            }}
          >
            {showRepositoryPill && <RepositoryPill sx={{maxWidth: '50%'}} repositoryPillData={headerData} />}
            {taskListStatusData && <TaskListStatus taskListStatus={taskListStatusData} />}
            <LinkedPullRequests pullRequestsData={headerData} isSticky={isSticky} />
          </Box>
        </Box>
      </ContentWrapper>
    </>
  )
}

try{ HeaderMetadata.displayName ||= 'HeaderMetadata' } catch {}